import React, { ChangeEventHandler } from 'react'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import TextField from '@mui/material/TextField'
import {
  deriveErrorMessage,
  shouldDisplayError,
} from '../../utils/formErrorHandlers'
import { useTheme } from '@mui/material/styles'

const CURRENCY = {
  USD: {
    ABBREVIATION: ' USD',
    DECIMAL_PLACES: 2,
    DECIMAL_SEPARATOR: '.',
    SYMBOL: '$',
    THOUSANDS_SEPARATOR: ',',
    THOUSANDS_GROUP_STYLE: 'thousand',
  },
}
const DECIMAL = '.'

const defaultSx = (theme: any) => ({
  textInputStyle: {
    width: '100%',
    fieldset: {
      borderRadius: theme.spacing(),
      border: '1px solid rgba(0, 0, 0, 0.2)',
      boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.07)',
    },
  },
  inputLabelStyle: {
    top: '-5px',
    color: '#000000B2',
    '&.Mui-focused': {
      visibility: 'hidden',
    },
  },
  mainInputStyle: {
    height: theme.spacing(5.25),
    padding: '0',
  },
  secondaryInputStyle: {
    height: theme.spacing(5.25),
    padding: theme.spacing(0, 1.5),
  },
})

export interface AmountInputProps {
  autofocus?: boolean
  currency?: typeof CURRENCY
  disabled?: boolean
  dataTestId?: string
  error?: boolean
  form?: UseFormReturn
  helperText?: string
  id?: string
  required?: boolean
  label?: string
  name: string
  onChange?: ChangeEventHandler
  rules?: object
  value?: string
}

const AmountInput = (props: AmountInputProps) => {
  const defaultForm = props.form || useForm()
  const {
    formState: { errors },
  } = defaultForm
  const formattedLabel = `${props.currency?.USD.SYMBOL}${
    props.label ? ` ${props.label}` : ''
  }`

  const theme = useTheme()
  const {
    mainInputStyle,
    secondaryInputStyle,
    inputLabelStyle,
    textInputStyle,
  } = defaultSx(theme)

  const handleChange = (values: { value: string }, onChange: Function) => {
    const { value } = values
    const maskedValue = value === DECIMAL ? '1.' : value

    onChange(maskedValue)
  }

  return (
    <Controller
      control={defaultForm?.control}
      name={props.name}
      rules={props.rules}
      render={({ field: { onChange, value, name } }) => {
        return (
          <NumberFormat
            allowNegative={false}
            autoFocus={props.autofocus}
            customInput={TextField}
            decimalScale={2}
            defaultValue={props.value}
            disabled={props.disabled}
            error={shouldDisplayError(errors, name, props.error)}
            helperText={deriveErrorMessage(errors, name, props?.helperText)}
            inputProps={{
              sx: mainInputStyle,
              'data-testid': props?.dataTestId,
            }}
            InputProps={{
              sx: secondaryInputStyle,
            }}
            InputLabelProps={{
              shrink: false,
              sx: inputLabelStyle,
            }}
            label={value?.length ? '' : formattedLabel}
            name={props.name}
            onValueChange={values => handleChange(values, onChange)}
            prefix={props.currency?.USD.SYMBOL}
            sx={textInputStyle}
            thousandSeparator
            value={value}
          />
        )
      }}
    />
  )
}

AmountInput.defaultProps = {
  currency: CURRENCY,
  dataTestId: 'plastiqBaseComponentAmountInput',
  name: 'amountInput',
  required: false,
}

export default AmountInput
